import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from '@mui/material';
import { ImmutableDataTable, UserActionsMenu } from '@components';
import type { UserActionsMenuOption } from '@components';
import { Row } from '../../../../components/ImmutableDataTable/ImmutableDataTable';
import { TOAST_TYPES, USER_STATUS } from '@constants/constants';
import { setToastMessage } from '@stores/App/slice';
import { genericTexts } from '@utils/translations/en';
import { UserManagementTableRow, useUserManagementTable } from '../hooks/useUserManagementTable';
import { getUserManagementTableColumns } from '../utils/userManagementTableColumns';
import useDeleteUser from '@hooks/api/useDeleteUser';
import { useLazyGetAccessTemplateTypesListQuery } from '@api/userList';
import useRetryCreateUser from '@hooks/api/useRetryCreateUser';

enum MenuAction {
  EDIT_ACCESS = 'Edit access',
  CLONE_ACCESS = 'Clone Access',
  RETRY_INVITE = 'Retry Invite',
  DELETE_USER = 'Delete User',
}

type PvxTableProps = {
  pageNumber: number;
  pageSize: number;
  searchQuery: string;
  onPageReset: () => void;
  onTotalChange: (total: number) => void;
  onEdit: (row: UserManagementTableRow) => void;
  onClone: (row: UserManagementTableRow) => void;
};

const PvxTable = ({
  pageNumber,
  pageSize,
  searchQuery,
  onPageReset,
  onTotalChange,
  onEdit,
  onClone,
}: PvxTableProps) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const {
    rows,
    isLoading,
    total,
    refetch,
    sort,
    sortOrder,
    handleSort,
    roleFilter,
    handleRoleFilterChange,
    accessTemplateTypeFilter,
    handleAccessTemplateTypeFilterChange,
    statusFilter,
    handleStatusFilterChange,
  } = useUserManagementTable({
    pageNumber,
    pageSize,
    searchQuery,
    onPageReset,
  });

  useEffect(() => {
    onTotalChange(total);
  }, [total, onTotalChange]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<UserManagementTableRow | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
  const [accessTemplateTypeFilterOptions, setAccessTemplateTypeFilterOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const { deleteUserById, isLoading: isDeletingUser } = useDeleteUser();
  const { reCreateUser } = useRetryCreateUser();

  const [getAccessTemplateTypesList] = useLazyGetAccessTemplateTypesListQuery();

  const fetchAccessTemplateTypesList = useCallback(async () => {
    try {
      const response = await getAccessTemplateTypesList().unwrap();
      const items = response?.data ?? [];
      const filterOptions = items.map((item) => ({
        label: item.label,
        value: item.internalUserAccessTemplateType,
      }));
      setAccessTemplateTypeFilterOptions(filterOptions);
    } catch (error) {
      console.error('Error while fetching access template types list:', error);
    }
  }, [getAccessTemplateTypesList]);

  useEffect(() => {
    fetchAccessTemplateTypesList();
  }, []);

  const handleRetryInvite = async (row: UserManagementTableRow) => {
    const res = await reCreateUser({ id: row.id });

    if (!res?.code) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: 'Error while retrying invite',
        }),
      );
    } else refetch();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: UserManagementTableRow) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedUser(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRowClick = (row: Row<UserManagementTableRow>) => {
    if (row.userStatus === USER_STATUS.PENDING) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: `Could not create ${row.user}, please retry.`,
        }),
      );
      return;
    }

    onEdit(row);
  };

  const handleDeleteUser = async (id: string | undefined) => {
    if (!id) return;

    try {
      const res = await deleteUserById({ id });
      dispatch(
        setToastMessage({
          type: Boolean(res?.code) ? TOAST_TYPES.SUCCESS : TOAST_TYPES.ERROR,
          message: Boolean(res?.code)
            ? `${selectedUser?.user} deleted !`
            : `Failed to delete ${selectedUser?.user}, please try again !`,
        }),
      );
      refetch();
    } catch (error) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: `Failed to delete ${selectedUser?.user}, please try again !`,
        }),
      );
    }
    setShowDeleteConfirmation(false);
  };

  const menuOptions: UserActionsMenuOption[] = selectedUser
    ? [
        {
          label: MenuAction.EDIT_ACCESS,
          onClick: () => {
            handleMenuClose();
            onEdit(selectedUser);
          },
        },
        {
          label: MenuAction.CLONE_ACCESS,
          onClick: () => {
            handleMenuClose();
            onClone(selectedUser);
          },
        },
        ...(selectedUser.userStatus === USER_STATUS.PENDING
          ? [
              {
                label: MenuAction.RETRY_INVITE,
                onClick: () => {
                  handleMenuClose();
                  handleRetryInvite(selectedUser);
                },
              },
            ]
          : []),
        {
          label: MenuAction.DELETE_USER,
          isDestructive: true,
          onClick: () => {
            handleMenuClose();
            setShowDeleteConfirmation(true);
          },
        },
      ]
    : [];

  const columns = useMemo(
    () =>
      getUserManagementTableColumns({
        onActionClick: handleMenuOpen,
        sort,
        sortOrder,
        onSort: handleSort,
        roleFilter,
        onRoleFilterChange: handleRoleFilterChange,
        accessTemplateTypeFilter,
        accessTemplateTypeFilterOptions,
        onAccessTemplateTypeFilterChange: handleAccessTemplateTypeFilterChange,
        statusFilter,
        onStatusFilterChange: handleStatusFilterChange,
      }),
    [
      sort,
      sortOrder,
      handleSort,
      roleFilter,
      handleRoleFilterChange,
      accessTemplateTypeFilter,
      accessTemplateTypeFilterOptions,
      handleAccessTemplateTypeFilterChange,
      statusFilter,
      handleStatusFilterChange,
    ],
  );

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      {selectedUser?.id && (
        <UserActionsMenu anchorEl={anchorEl} onClose={handleMenuClose} options={menuOptions} />
      )}
      <Box sx={{ '& .MuiTableBody-root .MuiTableCell-root': { border: '1px solid #2F736E1F' } }}>
        <ImmutableDataTable<UserManagementTableRow>
          data={rows}
          columns={columns}
          meta={{
            totalCount: total,
            currentPage: pageNumber,
            pageSize,
          }}
          onViewRow={handleRowClick}
          isLoading={isLoading}
        />
      </Box>
      <Dialog
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        maxWidth={false}
        sx={{
          '& .MuiDialog-paper': {
            width: '400px',
            borderRadius: '28px',
            padding: '24px',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '24px',
            lineHeight: '32px',
            fontWeight: 700,
            color: '#162936',
            fontFamily: 'Manrope, sans-serif',
            p: 0,
          }}
        >
          Delete user
        </DialogTitle>
        <DialogContent sx={{ p: 0, mt: 1 }}>
          <Typography
            sx={{
              fontSize: '14px',
              lineHeight: '20px',
              fontWeight: 500,
              color: '#6C6C6C',
            }}
          >
            Are you sure you want to delete this user ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 0, mt: 3, gap: 1 }}>
          <Button
            onClick={() => setShowDeleteConfirmation(false)}
            disabled={isDeletingUser}
            sx={{
              background: theme.palette.common.white,
              borderRadius: '100px',
              color: theme.palette.primary.main,
              fontWeight: 700,
              fontSize: '0.875rem',
              textTransform: 'none',
            }}
          >
            {genericTexts.Cancel}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleDeleteUser(selectedUser?.id)}
            disabled={isDeletingUser}
            sx={{
              background: theme.palette.primary.main,
              borderRadius: '100px',
              color: theme.palette.common.white,
              fontWeight: 700,
              fontSize: '0.875rem',
              textTransform: 'none',
              '&:hover': {
                background: theme.palette.primary.main,
              },
            }}
          >
            {isDeletingUser ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PvxTable;
